import RenderArticleBlocks from "@blocks/RenderArticleBlocks/RenderArticleBlocks";
import WrapperBlock from "@container/WrapperBlock";
import { CompanionAdsImu2 } from "@elements/Advertisement/variant/CompanionAdImu";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import { scrollFlags } from "@elements/BeyondWordsPlayer/events";
import fetchTMApi from "@helper/fetchTMApi";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import { formatDate } from "@helper/useDateFormat";
import {
  getWindowOrigin,
  isArrayWithElements,
  isClientSide,
} from "@helper/utils";
import { useAudioAnalytics } from "@hooks/useAnalyticsCall/useAudioAnalytics";
import {
  useComscoreCall,
  useTMCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useVisibleArticle from "@hooks/useVisibleArticle";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import ArticleContentCard from "@routes/Article/ArticleContentCard";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetValue } from "@typings/Ads";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import { Fragment, ReactElement, useEffect, useState } from "react";

type ArticleInfiniteScrollProps = {
  mainArticle: ProcessedArticleData;
  pageAdTargetValue: PageAdTargetValue;
  onNewArticle: (
    article: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => void;
  hasTaboola?: boolean;
  gsChannels?: string[];
  isCuePreview?: boolean;
};

export type ArticleVariantData = {
  payload: {
    article: {
      variant: string;
    };
  };
};

export default function ArticleInfiniteScroll({
  mainArticle,
  pageAdTargetValue,
  onNewArticle,
  hasTaboola = true,
  gsChannels,
  isCuePreview = false,
}: ArticleInfiniteScrollProps): ReactElement {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [newArticleGaLoaded, setNewArticleGaLoaded] = useState<boolean>(false);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>([mainArticle.id]),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [visibleArticleId, setVisibleArticleId] = useState<string>(
    mainArticle.id,
  );

  // Trigger useVisibleArticle when LoadMoreData changes
  const visibleArticleIdFromHook = useVisibleArticle(visibleArticleId);

  const [currentPath, setCurrentPath] = useState<string>(
    mainArticle.urlPath || "",
  );
  const [latestArticle, setLatestArticle] = useState<ProcessedArticleData>();

  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const dataToComScore = useComscoreCall();

  const filterArticlesBasedOn = mainArticle.sectionName;

  const origin = getWindowOrigin();

  const fetchNextStoryData = async (
    requestData: MoreArticlesRequestData,
  ): Promise<ProcessedArticleData[]> => {
    const queryParams = new URLSearchParams({
      section: requestData.filterArticlesBasedOn as string,
      excludeIds: requestData.excludeIds as string,
    }).toString();
    const response: ProcessedArticleData[] = await fetchTMApi(
      `get-article?${queryParams}`,
      "GET",
    );

    return response;
  };
  const audioTrackingEvent = useAudioAnalytics();
  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? mainArticle : loadMoreData.at(-1);

    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn,
      excludeIds: [...renderedArticleIds].join(","),
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchNextStoryData;

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: 1,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);

    if (isArrayWithElements(response)) {
      const article = response[0];
      setVisibleArticleId(article.id);

      if (
        typeof window.beyondWordsPlayerInstance !== "undefined" &&
        shouldShowTTSPlayer(article.datetime)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        window.beyondWordsPlayerInstance.clientSideEnabled = false;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        window.beyondWordsPlayerInstance.sourceId = article.id;
        // Keep track of whether the player was playing when the identifiers change.
        let previousState: string;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        window.beyondWordsPlayerInstance.addEventListener(
          "IdentifiersChanged",
          () => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            previousState = window.beyondWordsPlayerInstance.playbackState;
          },
        );

        // This event is fired when the player has loaded a new audio file.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        window.beyondWordsPlayerInstance.addEventListener(
          "DurationUpdated",
          () => {
            if (previousState === "playing" || scrollFlags.isProgrammatic) {
              /* eslint-disable @typescript-eslint/no-unsafe-member-access */
              const audioListenedTime =
                Number(window.beyondWordsPlayerInstance.currentTime) || 0;
              const audioId: string = `${String(window.beyondWordsPlayerInstance.projectId)}::${String(article.id)}`;
              const audioDuration =
                Number(window.beyondWordsPlayerInstance.duration) || 0;
              const audioListenedpercentage =
                audioDuration === 0
                  ? 0
                  : Math.floor((audioListenedTime / audioDuration) * 100);
              const audioSpeed =
                Number(window.beyondWordsPlayerInstance.playbackRate) || 1;
              /* eslint-enable @typescript-eslint/no-unsafe-member-access */
              audioTrackingEvent(
                "audio_start",
                article.title,
                audioId,
                audioListenedTime,
                audioDuration,
                audioListenedpercentage,
                audioSpeed,
                "yes",
              );
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              window.beyondWordsPlayerInstance.playbackState = "playing";
              scrollFlags.isProgrammatic = false; // Reset the flag after playback starts
            }
          },
        );
      }

      onNewArticle(article, pageNumber + 1, false);
      setLatestArticle(article);

      // Load GA events on infinite scroll.
      TmCustomGaEvent({
        event: "infiniteScrollArticle",
        articleid: article.id,
        byline: article.authors
          ? article.authors.map((author) => author.name).join(", ")
          : "",
        contenttype: "article",
        keyword: article.tags
          ? article.tags.map((tags) => tags.name).join(", ")
          : "",
        landingPage: origin + mainArticle.urlPath,
        nextNodeTitle: article.title,
        nextNodeUrl: origin + article.urlPath,
        pubdate: formatDate(article.dateCreated, "yyyy-MM-dd HH:mm:ss"),
        pagination: pageNumber == 0 ? 1 : pageNumber,
      });
    } else {
      setHasMoreStories(false);
    }

    return response;
  };

  useEffect(() => {
    if (latestArticle && newArticleGaLoaded) {
      TmCustomGaEvent({
        event: "virtualPV",
        articleid: latestArticle.id,
        byline: latestArticle.authors
          ? latestArticle.authors.map((author) => author.name).join(", ")
          : "",
        chapter1: "",
        contenttype: "article",
        cue_articleid: latestArticle.id,
        keyword: latestArticle.tags
          ? latestArticle.tags.map((tags) => tags.name).join(", ")
          : "",
        landingPage: origin + mainArticle.urlPath,
        level2: latestArticle.sectionName,
        nextNodeTitle: latestArticle.title,
        nextNodeUrl: origin + latestArticle.urlPath,
        pubdate: formatDate(latestArticle.dateCreated, "yyyy-MM-dd HH:mm:ss"),
        title: mainArticle.title,
      });
      dataToComScore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newArticleGaLoaded]);

  useEffect(() => {
    if (!isClientSide || !visibleArticleId) return;
    const allArticles = [mainArticle, ...loadMoreData];
    const visibleArticleIndex = allArticles.findIndex(
      (article) => article.id === visibleArticleId,
    );

    const articleData =
      visibleArticleIndex !== -1 ? allArticles[visibleArticleIndex] : undefined;
    if (articleData) {
      const newPath = articleData.urlPath;
      if (newPath !== currentPath) {
        window.history.pushState("article", articleData.title, newPath);
        setCurrentPath(newPath || "");
        if (
          typeof window.beyondWordsPlayerInstance !== "undefined" &&
          shouldShowTTSPlayer(articleData.datetime)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          window.beyondWordsPlayerInstance.clientSideEnabled = false;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          window.beyondWordsPlayerInstance.sourceId = articleData.id;
        }
        onNewArticle(articleData, visibleArticleIndex, true);
      }
    }
  }, [visibleArticleId, loadMoreData, mainArticle, onNewArticle, currentPath]);

  useEffect(() => {
    if (!isClientSide) return;
    if (visibleArticleIdFromHook) {
      setVisibleArticleId(visibleArticleIdFromHook);
      setNewArticleGaLoaded(true);
    }
  }, [visibleArticleIdFromHook]);

  const { isScreenLG } = useWindowSize();
  return (
    <>
      {[...loadMoreData].map((context, index) => (
        <Fragment key={index}>
          <div id={context.id} className="article-card mt-10">
            {isScreenLG && (
              <CompanionAdsLb
                uniqueSectionName={filterArticlesBasedOn}
                index={index + 2}
                pageAdTargetType={pageAdTargetValue}
                gsChannels={gsChannels}
                articleSlotTarget={[
                  {
                    key: "tmarticleid",
                    value: context.id,
                  },
                  {
                    key: "tmtags",
                    value:
                      context.tags?.map((tags) => tags.name).join(", ") || "",
                  },
                ]}
                rootClassName={
                  "bg-red-500 lg:mb-10 lg:pt-xm lg:pb-5 relative lg:w-[100vw] xl:left-[-95px] xl:max-w-[1440px] xl:w-[calc(100%+190px)]"
                }
              />
            )}
            <WrapperBlock className="hover:text flex flex-col lg:flex-row justify-between">
              <ArticleContentCard
                articleData={context}
                pageNumber={index + 1}
                isInfiniteScrollArticle={true}
                hasTaboola={hasTaboola}
                gsChannels={gsChannels}
                articleSlotTarget={[
                  { key: "page_number", value: (index + 2).toString() },
                  {
                    key: "tmarticleid",
                    value: context.id,
                  },
                  {
                    key: "tmtags",
                    value:
                      context.tags?.map((tags) => tags.name).join(", ") || "",
                  },
                ]}
                isCuePreview={isCuePreview}
              />
              <div className="lg:w-4/12 lg:pl-xs lg:pr-xxs lg:min-h-[800px] relative lg:justify-center lg:flex">
                {!isScreenLG ? (
                  <>
                    {/* Render blocks based on blockIndex */}
                    <RenderArticleBlocks
                      index={index + 1}
                      pageNumber={index + 1}
                      pageUrl={context.urlPath ? context.urlPath : ""}
                      articleData={context}
                      pageAdTargetValue={pageAdTargetValue}
                      gsChannels={gsChannels}
                      hasTaboola={hasTaboola}
                    />
                  </>
                ) : (
                  <CompanionAdsImu2
                    uniqueSectionName={context.sectionName}
                    pageAdTargetType={pageAdTargetValue}
                    pageNumber={index + 1}
                    rootClassName={
                      "lg:absolute lg:top-0 lg:bottom-1/4 lg:!h-auto"
                    }
                    gsChannels={gsChannels}
                    articleSlotTarget={[
                      {
                        key: "page_number",
                        value: (index + 1).toString(),
                      },
                      {
                        key: "tmarticleid",
                        value: context.id,
                      },
                      {
                        key: "tmtags",
                        value:
                          context.tags?.map((tags) => tags.name).join(", ") ||
                          "",
                      },
                    ]}
                  />
                )}
              </div>
            </WrapperBlock>
          </div>
        </Fragment>
      ))}
      <LoadMore
        rootClassName="my-4"
        onLoadMore={handleLoadMore}
        hasMore={hasMoreStories}
        loadDefaultGaEvent={false}
        landingPage={`${origin}${mainArticle.urlPath}`}
        mobileInfiniteScroll={true}
      />
    </>
  );
}
